/** @format */

/**
 * AudioGO (Pandora) conversion pixels.
 *
 * Paste the HTML snippet from AudioGO into the matching env var(s), then redeploy.
 * Leave unset to disable. Snippets are injected once per page view on matching routes.
 *
 *   GATSBY_AUDIOGO_PIXEL_ALL_PAGES   — site-wide (use Unique conversions in AudioGO reporting)
 *   GATSBY_AUDIOGO_PIXEL_HOME        — AudioGO "Home Page Visit" on /
 *   GATSBY_AUDIOGO_PIXEL_GET_QUOTE   — AudioGO "Page Visit" on /get-quote/ (no get-quote type in AudioGO)
 *   GATSBY_AUDIOGO_PIXEL_CHECKOUT    — AudioGO "Checkout" on /checkout/
 *   GATSBY_AUDIOGO_PIXEL_COMPLETE    — AudioGO "Purchase" on /complete/
 */

export type AudiogoPixelTarget = {
  id: string;
  path: string;
  exact?: boolean;
  snippet: string;
};

function snippetFromEnv(value: string | undefined): string {
  return (value ?? "").trim();
}

function normalizePath(pathname: string): string {
  const path = pathname.split("?")[0].split("#")[0];
  if (path === "/") return "/";
  return path.replace(/\/+$/, "");
}

export function getAudiogoPixelTargets(): AudiogoPixelTarget[] {
  const configured: Array<{
    id: string;
    path: string;
    exact?: boolean;
    env: string | undefined;
  }> = [
    {
      id: "all-pages",
      path: "*",
      env: process.env.GATSBY_AUDIOGO_PIXEL_ALL_PAGES,
    },
    { id: "home", path: "/", exact: true, env: process.env.GATSBY_AUDIOGO_PIXEL_HOME },
    {
      id: "get-quote",
      path: "/get-quote",
      env: process.env.GATSBY_AUDIOGO_PIXEL_GET_QUOTE,
    },
    {
      id: "checkout",
      path: "/checkout",
      env: process.env.GATSBY_AUDIOGO_PIXEL_CHECKOUT,
    },
    {
      id: "complete",
      path: "/complete",
      env: process.env.GATSBY_AUDIOGO_PIXEL_COMPLETE,
    },
  ];

  return configured
    .map(({ id, path, exact, env }) => ({
      id,
      path,
      exact,
      snippet: snippetFromEnv(env),
    }))
    .filter((target) => target.snippet.length > 0);
}

export function pathMatchesTarget(
  pathname: string,
  target: AudiogoPixelTarget
): boolean {
  if (target.path === "*") return true;

  const current = normalizePath(pathname);
  const targetPath = normalizePath(target.path);

  if (target.exact) return current === targetPath;
  return current === targetPath || current.startsWith(`${targetPath}/`);
}

export function getAudiogoPixelsForPath(pathname: string): AudiogoPixelTarget[] {
  return getAudiogoPixelTargets().filter((target) =>
    pathMatchesTarget(pathname, target)
  );
}

let activePathname = "";
const firedTargetIds = new Set<string>();

function injectPixelSnippet(snippet: string, targetId: string): void {
  const container = document.createElement("div");
  container.setAttribute("data-audiogo-pixel", targetId);
  container.innerHTML = snippet;

  container.querySelectorAll("script").forEach((oldScript) => {
    const script = document.createElement("script");
    for (const name of oldScript.getAttributeNames()) {
      const value = oldScript.getAttribute(name);
      if (value !== null) script.setAttribute(name, value);
    }
    script.textContent = oldScript.textContent;
    oldScript.replaceWith(script);
  });

  document.body.appendChild(container);
}

/** Fire matching AudioGO pixels once per client-side page view. */
export function fireAudiogoPixels(pathname: string): void {
  if (typeof window === "undefined") return;

  const normalizedPathname = normalizePath(pathname);
  if (normalizedPathname !== activePathname) {
    activePathname = normalizedPathname;
    firedTargetIds.clear();
  }

  for (const pixel of getAudiogoPixelsForPath(normalizedPathname)) {
    if (firedTargetIds.has(pixel.id)) continue;
    firedTargetIds.add(pixel.id);
    injectPixelSnippet(pixel.snippet, pixel.id);
  }
}
